/**
 * Store Directory - JavaScript
 * 
 * Handles all functionality for the store directory page:
 * - Supabase client initialization
 * - Fetching cities and stores from Supabase
 * - Rendering city cards and store lists
 * - Navigation between views
 * - Error handling and loading states
 * 
 * @version 1.0.0
 */

(function() {
  'use strict';

  // ============================================================================
  // Configuration & State
  // ============================================================================

  let supabase;
  let citiesData = [];
  let selectedCity = null;
  let allStores = []; // Store all fetched stores
  let filteredStores = []; // Store filtered results
  
  // Get configuration from Liquid settings
  const config = window.STORE_DIRECTORY_CONFIG || {};
  const SUPABASE_URL = config.supabaseUrl;
  const SUPABASE_ANON_KEY = config.supabaseAnonKey;
  const ICON_BASE_URL = config.iconBaseUrl;

  // ============================================================================
  // DOM Elements
  // ============================================================================

  const elements = {
    loadingScreen: document.getElementById('loadingScreen'),
    errorScreen: document.getElementById('errorScreen'),
    errorMessage: document.getElementById('errorMessage'),
    retryButton: document.getElementById('retryButton'),
    citySelectionView: document.getElementById('citySelectionView'),
    popularCitiesSection: document.getElementById('popularCitiesSection'),
    popularCitiesGrid: document.getElementById('popularCitiesGrid'),
    otherCitiesSection: document.getElementById('otherCitiesSection'),
    otherCitiesGrid: document.getElementById('otherCitiesGrid'),
    storesView: document.getElementById('storesView'),
    backButton: document.getElementById('backButton'),
    selectedCityName: document.getElementById('selectedCityName'),
    storesLoading: document.getElementById('storesLoading'),
    storesListContainer: document.getElementById('storesListContainer'),
    storesTableBody: document.getElementById('storesTableBody'),
    storeSearch: document.getElementById('storeSearch'),
    locationFilter: document.getElementById('locationFilter'),
    typeFilter: document.getElementById('typeFilter'),
    noResults: document.getElementById('noResults'),
    emptyState: document.getElementById('emptyState')
  };

  // ============================================================================
  // Utility Functions
  // ============================================================================

  /**
   * Show a specific view and hide others
   */
  function showView(viewName) {
    elements.loadingScreen.style.display = 'none';
    elements.errorScreen.style.display = 'none';
    elements.citySelectionView.style.display = 'none';
    elements.storesView.style.display = 'none';

    switch(viewName) {
      case 'loading':
        elements.loadingScreen.style.display = 'flex';
        break;
      case 'error':
        elements.errorScreen.style.display = 'flex';
        break;
      case 'citySelection':
        elements.citySelectionView.style.display = 'block';
        break;
      case 'stores':
        elements.storesView.style.display = 'block';
        break;
    }
  }

  /**
   * Show error message
   */
  function showError(message) {
    console.error('Store Directory Error:', message);
    elements.errorMessage.textContent = message;
    showView('error');
  }

  /**
   * Construct icon URL from city slug
   */
  function constructIconUrl(citySlug) {
    if (!ICON_BASE_URL) {
      console.warn('Icon Base URL not configured');
      return null;
    }
    // Ensure base URL ends with /
    const baseUrl = ICON_BASE_URL.endsWith('/') ? ICON_BASE_URL : ICON_BASE_URL + '/';
    const iconUrl = baseUrl + citySlug + '.webp';
    console.log(`Icon URL for ${citySlug}:`, iconUrl);
    return iconUrl;
  }

  /**
   * Get first letter of city name for fallback
   */
  function getCityInitial(cityName) {
    return cityName ? cityName.charAt(0).toUpperCase() : '?';
  }

  /**
   * Get color for store type badge
   */
  function getTypeColor(typeName) {
    const typeColors = {
      'retail': '#e3f2fd',
      'restaurant': '#fff3e0',
      'bar': '#f3e5f5',
      'airport': '#e8f5e9',
      'hotel': '#fce4ec'
    };
    
    const normalizedType = typeName ? typeName.toLowerCase() : '';
    return typeColors[normalizedType] || '#f0f0f0';
  }

  /**
   * Throttle function to limit execution frequency
   */
  function throttle(func, delay) {
    let timeoutId;
    let lastExecTime = 0;
    
    return function(...args) {
      const currentTime = Date.now();
      const timeSinceLastExec = currentTime - lastExecTime;
      
      clearTimeout(timeoutId);
      
      if (timeSinceLastExec >= delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - timeSinceLastExec);
      }
    };
  }

  /**
   * Filter stores based on search and type
   */
  function filterStores(searchTerm, location, storeType) {
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    
    return allStores.filter(store => {
      // Filter by search term (store name)
      const matchesSearch = !lowerSearchTerm || 
        store.name.toLowerCase().includes(lowerSearchTerm);
      
      // Filter by location
      const matchesLocation = !location || 
        store.localities?.name === location;
      
      // Filter by store type
      const matchesType = !storeType || 
        store.store_types?.name === storeType;
      
      return matchesSearch && matchesLocation && matchesType;
    });
  }

  // ============================================================================
  // Supabase Functions
  // ============================================================================

  /**
   * Initialize Supabase client
   */
  function initSupabase() {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      throw new Error('Supabase configuration is missing. Please configure Supabase URL and Anon Key in the section settings.');
    }

    if (!window.supabase || !window.supabase.createClient) {
      throw new Error('Supabase client library failed to load. Please check your internet connection.');
    }

    try {
      supabase = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
      console.log('✓ Supabase client initialized successfully');
    } catch (error) {
      throw new Error('Failed to initialize Supabase client: ' + error.message);
    }
  }

  /**
   * Fetch all cities from Supabase
   */
  async function fetchCities() {
    try {
      const { data, error } = await supabase
        .from('cities')
        .select('id, name, slug, is_popular, display_order')
        .order('display_order');

      if (error) {
        console.error('Supabase Error:', error);
        throw new Error('Failed to fetch cities from database');
      }

      if (!data || data.length === 0) {
        throw new Error('No cities found in the database');
      }

      // Add icon URLs to cities
      data.forEach(city => {
        city.icon_url = constructIconUrl(city.slug);
      });

      console.log('✓ Fetched', data.length, 'cities');
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch stores for a specific city
   */
  async function fetchStores(citySlug) {
    try {
      const { data, error } = await supabase
        .from('stores')
        .select(`
          id,
          name,
          is_active,
          localities!inner (
            id,
            name,
            cities!inner (
              id,
              name,
              slug
            )
          ),
          store_types (
            id,
            name
          )
        `)
        .eq('localities.cities.slug', citySlug)
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Supabase Error:', error);
        throw new Error('Failed to fetch stores from database');
      }

      console.log('✓ Fetched', data?.length || 0, 'stores for', citySlug);
      return data || [];
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // Render Functions
  // ============================================================================

  /**
   * Render city selection cards
   */
  function renderCitySelection() {
    // Separate popular and other cities
    const popularCities = citiesData.filter(city => city.is_popular);
    const otherCities = citiesData.filter(city => !city.is_popular);

    // Render popular cities
    if (popularCities.length > 0) {
      elements.popularCitiesGrid.innerHTML = popularCities.map(city => createCityCard(city)).join('');
      elements.popularCitiesSection.style.display = 'block';
    }

    // Render other cities
    if (otherCities.length > 0) {
      elements.otherCitiesGrid.innerHTML = otherCities.map(city => createCityCard(city)).join('');
      elements.otherCitiesSection.style.display = 'block';
    }

    // Add click event listeners to city cards
    document.querySelectorAll('.city-card').forEach(card => {
      card.addEventListener('click', handleCityClick);
      // Add keyboard support
      card.addEventListener('keypress', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCityClick.call(this, e);
        }
      });
    });

    showView('citySelection');
  }

  /**
   * Create HTML for a city card
   */
  function createCityCard(city) {
    const initial = getCityInitial(city.name);
    
    return `
      <div class="city-card" data-city-slug="${city.slug}" data-city-name="${city.name}" tabindex="0" role="button" aria-label="Select ${city.name}">
        <div class="city-icon-wrapper">
          ${city.icon_url ? `
            <img 
              src="${city.icon_url}" 
              alt="${city.name}" 
              class="city-icon"
              onerror="console.warn('Failed to load icon:', '${city.icon_url}'); this.style.display='none'; this.nextElementSibling.style.display='flex';"
              onload="console.log('Successfully loaded icon:', '${city.icon_url}');"
            >
            <div class="city-icon-fallback" style="display: none;">
              ${initial}
            </div>
          ` : `
            <div class="city-icon-fallback">
              ${initial}
            </div>
          `}
        </div>
        <h3 class="city-name">${city.name}</h3>
      </div>
    `;
  }

  /**
   * Handle city card click
   */
  function handleCityClick(event) {
    const cityCard = event.currentTarget;
    const citySlug = cityCard.dataset.citySlug;
    const cityName = cityCard.dataset.cityName;

    if (!citySlug) return;

    // Find city data
    selectedCity = citiesData.find(city => city.slug === citySlug);
    if (!selectedCity) {
      console.error('City not found:', citySlug);
      return;
    }

    // Show stores view
    showStoresView(cityName);
    
    // Load stores
    loadStoresForCity(citySlug, cityName);
  }

  /**
   * Show stores view
   */
  function showStoresView(cityName) {
    elements.selectedCityName.textContent = cityName;
    
    // Reset stores view
    elements.storesLoading.style.display = 'flex';
    elements.storesListContainer.style.display = 'none';
    elements.emptyState.style.display = 'none';
    elements.noResults.style.display = 'none';
    
    // Reset search and filter
    if (elements.storeSearch) elements.storeSearch.value = '';
    if (elements.locationFilter) elements.locationFilter.value = '';
    if (elements.typeFilter) elements.typeFilter.value = '';
    
    showView('stores');
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Load and render stores for a city
   */
  async function loadStoresForCity(citySlug, cityName) {
    try {
      const stores = await fetchStores(citySlug);
      
      // Store all fetched stores
      allStores = stores;
      filteredStores = stores;
      
      if (stores.length === 0) {
        // Show empty state
        elements.storesLoading.style.display = 'none';
        elements.emptyState.style.display = 'flex';
      } else {
        // Populate filters
        populateLocationFilter(stores);
        populateTypeFilter(stores);
        
        // Render stores
        renderStoresTable(stores);
        elements.storesLoading.style.display = 'none';
        elements.storesListContainer.style.display = 'block';
      }
    } catch (error) {
      console.error('Error loading stores:', error);
      showError('Failed to load stores. Please try again.');
    }
  }

  /**
   * Populate the location filter dropdown with unique localities
   */
  function populateLocationFilter(stores) {
    const locations = new Set();
    stores.forEach(store => {
      if (store.localities?.name) {
        locations.add(store.localities.name);
      }
    });
    
    // Clear existing options except the first one
    elements.locationFilter.innerHTML = '<option value="">All Locations</option>';
    
    // Add unique locations
    Array.from(locations).sort().forEach(location => {
      const option = document.createElement('option');
      option.value = location;
      option.textContent = location;
      elements.locationFilter.appendChild(option);
    });
  }

  /**
   * Populate the type filter dropdown with unique store types
   */
  function populateTypeFilter(stores) {
    const types = new Set();
    stores.forEach(store => {
      if (store.store_types?.name) {
        types.add(store.store_types.name);
      }
    });
    
    // Clear existing options except the first one
    elements.typeFilter.innerHTML = '<option value="">All Types</option>';
    
    // Add unique types
    Array.from(types).sort().forEach(type => {
      const option = document.createElement('option');
      option.value = type;
      option.textContent = type;
      elements.typeFilter.appendChild(option);
    });
  }

  /**
   * Render stores table
   */
  function renderStoresTable(stores) {
    if (stores.length === 0) {
      elements.noResults.style.display = 'block';
      elements.storesTableBody.innerHTML = '';
      return;
    }
    
    elements.noResults.style.display = 'none';
    elements.storesTableBody.innerHTML = stores.map(store => createStoreRow(store)).join('');
  }

  /**
   * Create HTML for a store table row
   */
  function createStoreRow(store) {
    const localityName = store.localities?.name || 'Unknown Location';
    const typeName = store.store_types?.name || 'Store';

    return `
      <tr>
        <td class="store-name-cell">${store.name}</td>
        <td class="store-locality-cell">${localityName}</td>
        <td class="store-type-cell">${typeName}</td>
      </tr>
    `;
  }

  /**
   * Handle search input
   */
  const handleSearch = throttle(function() {
    const searchTerm = elements.storeSearch.value;
    const selectedLocation = elements.locationFilter.value;
    const selectedType = elements.typeFilter.value;
    
    filteredStores = filterStores(searchTerm, selectedLocation, selectedType);
    renderStoresTable(filteredStores);
  }, 300); // 300ms throttle delay

  /**
   * Handle location filter change
   */
  function handleLocationFilter() {
    const searchTerm = elements.storeSearch.value;
    const selectedLocation = elements.locationFilter.value;
    const selectedType = elements.typeFilter.value;
    
    filteredStores = filterStores(searchTerm, selectedLocation, selectedType);
    renderStoresTable(filteredStores);
  }

  /**
   * Handle type filter change
   */
  function handleTypeFilter() {
    const searchTerm = elements.storeSearch.value;
    const selectedLocation = elements.locationFilter.value;
    const selectedType = elements.typeFilter.value;
    
    filteredStores = filterStores(searchTerm, selectedLocation, selectedType);
    renderStoresTable(filteredStores);
  }

  // ============================================================================
  // Navigation Functions
  // ============================================================================

  /**
   * Handle back button click
   */
  function handleBackButton() {
    selectedCity = null;
    allStores = [];
    filteredStores = [];
    showView('citySelection');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Handle retry button click
   */
  function handleRetry() {
    init();
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  /**
   * Initialize the application
   */
  async function init() {
    console.log('Initializing Store Directory...');
    
    // Show loading screen
    showView('loading');

    try {
      // Initialize Supabase
      initSupabase();

      // Fetch cities
      citiesData = await fetchCities();

      // Render city selection
      renderCitySelection();

    } catch (error) {
      console.error('Initialization Error:', error);
      showError(error.message || 'Failed to load store directory. Please try again.');
    }
  }

  /**
   * Setup event listeners
   */
  function setupEventListeners() {
    // Back button
    elements.backButton.addEventListener('click', handleBackButton);
    
    // Retry button
    elements.retryButton.addEventListener('click', handleRetry);
    
    // Search input
    if (elements.storeSearch) {
      elements.storeSearch.addEventListener('input', handleSearch);
    }
    
    // Location filter
    if (elements.locationFilter) {
      elements.locationFilter.addEventListener('change', handleLocationFilter);
    }
    
    // Type filter
    if (elements.typeFilter) {
      elements.typeFilter.addEventListener('change', handleTypeFilter);
    }
  }

  // ============================================================================
  // Start Application
  // ============================================================================

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setupEventListeners();
      init();
    });
  } else {
    setupEventListeners();
    init();
  }

})();
